class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) {
      selectedOption.click();
    } else if (this.getQuery().length) {
      this.input.form.submit();
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const searchParams = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
      'resources[type]': 'product,query',
      'resources[limit]': '8',
      'resources[options][prefix]': 'last',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
    });

    fetch(`${routes.predictive_search_url}?${searchParams.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.renderFuzzySuggestions(this.searchTerm);

    this.setLiveRegionResults();
    this.open();
  }

  async renderFuzzySuggestions(searchTerm) {
    const normalizedTerm = this.normalizeSearchText(searchTerm);

    if (normalizedTerm.length < 2) return;

    const matches = await this.getFuzzyMatches(normalizedTerm);

    if (!matches.length || this.normalizeSearchText(this.searchTerm) !== normalizedTerm) return;

    const results = this.querySelector('#predictive-search-results');
    if (!results) return;

    let wrapper = this.querySelector('#predictive-search-results-groups-wrapper');

    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.id = 'predictive-search-results-groups-wrapper';
      wrapper.className = 'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-products';
      results.insertBefore(wrapper, results.querySelector('.predictive-search__loading-state'));
    }

    wrapper.querySelector('[data-fuzzy-suggestions]')?.remove();
    wrapper.insertAdjacentHTML('afterbegin', this.buildFuzzySuggestionsMarkup(matches, searchTerm));
  }

  async getFuzzyMatches(normalizedTerm) {
    const index = await this.loadFuzzyIndex();
    const maxDistance = normalizedTerm.length < 5 ? 1 : normalizedTerm.length < 8 ? 2 : 3;
    const productMatches = index.products
      .map((product) => ({
        ...product,
        score: this.getBestFuzzyScore(normalizedTerm, product.searchWords, product.searchText, maxDistance),
      }))
      .filter((product) => product.score < 99)
      .sort((a, b) => a.score - b.score || a.title.length - b.title.length)
      .slice(0, 4);

    const termMatches = index.terms
      .map((term) => ({
        term,
        score: this.getWordScore(normalizedTerm, term, maxDistance),
      }))
      .filter((entry) => entry.score < 99)
      .sort((a, b) => a.score - b.score || a.term.length - b.term.length)
      .slice(0, 4);

    return [...termMatches, ...productMatches].slice(0, 6);
  }

  loadFuzzyIndex() {
    if (!window.mykothekeSearchIndexPromise) {
      window.mykothekeSearchIndexPromise = fetch('/products.json?limit=250')
        .then((response) => (response.ok ? response.json() : { products: [] }))
        .then((data) => {
          const termMap = new Map();
          const products = (data.products || []).map((product) => {
            const searchText = this.normalizeSearchText(
              [product.title, product.vendor, product.product_type, (product.tags || []).join(' ')].join(' ')
            );
            const searchWords = searchText.split(' ').filter((word) => word.length > 2);

            searchWords.forEach((word) => {
              if (!termMap.has(word)) termMap.set(word, word);
            });

            return {
              title: product.title,
              url: `/products/${product.handle}`,
              image: product.images?.[0]?.src || '',
              searchText,
              searchWords,
            };
          });

          return {
            products,
            terms: Array.from(termMap.values()).filter((term) => term.length > 2),
          };
        })
        .catch(() => ({ products: [], terms: [] }));
    }

    return window.mykothekeSearchIndexPromise;
  }

  getBestFuzzyScore(normalizedTerm, words, searchText, maxDistance) {
    if (searchText.includes(normalizedTerm)) return 0;

    return words.reduce((bestScore, word) => {
      return Math.min(bestScore, this.getWordScore(normalizedTerm, word, maxDistance));
    }, 99);
  }

  getWordScore(normalizedTerm, word, maxDistance) {
    if (word.startsWith(normalizedTerm)) return 1;
    if (word.includes(normalizedTerm)) return 2;

    const comparison = word.slice(0, Math.max(normalizedTerm.length, Math.min(word.length, normalizedTerm.length + 1)));
    const distance = this.getLevenshteinDistance(normalizedTerm, comparison);

    return distance <= maxDistance ? 3 + distance : 99;
  }

  getLevenshteinDistance(firstWord, secondWord) {
    const distances = Array.from({ length: secondWord.length + 1 }, (_, index) => index);

    for (let firstIndex = 1; firstIndex <= firstWord.length; firstIndex++) {
      let previousDistance = distances[0];
      distances[0] = firstIndex;

      for (let secondIndex = 1; secondIndex <= secondWord.length; secondIndex++) {
        const temporaryDistance = distances[secondIndex];
        distances[secondIndex] =
          firstWord[firstIndex - 1] === secondWord[secondIndex - 1]
            ? previousDistance
            : Math.min(previousDistance, distances[secondIndex], distances[secondIndex - 1]) + 1;
        previousDistance = temporaryDistance;
      }
    }

    return distances[secondWord.length];
  }

  normalizeSearchText(text) {
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9äöüß\s-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  buildFuzzySuggestionsMarkup(matches, searchTerm) {
    const safeInputId = this.escapeHTML(this.input.id || 'search');
    const items = matches
      .map((match, index) => {
        const id = `predictive-search-fuzzy-${safeInputId}-${index}`;

        if (match.term) {
          const searchUrl = window.routes?.search_url || '/search';
          const queryUrl = `${searchUrl}?type=product&options%5Bprefix%5D=last&q=${encodeURIComponent(match.term)}`;
          return `
            <li id="${id}" class="predictive-search__list-item" role="option" aria-selected="false">
              <a href="${queryUrl}" class="predictive-search__item predictive-search__item--term link link--text" tabindex="-1">
                <div class="predictive-search__item-content predictive-search__item-content--centered">
                  <span class="predictive-search__fuzzy-label">Vorschlag</span>
                  <p class="predictive-search__item-heading h5">${this.escapeHTML(match.term)}</p>
                </div>
              </a>
            </li>`;
        }

        return `
          <li id="${id}" class="predictive-search__list-item" role="option" aria-selected="false">
            <a href="${this.escapeHTML(match.url)}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
              ${
                match.image
                  ? `<img class="predictive-search__image" src="${this.escapeHTML(match.image)}" alt="" width="44" height="44">`
                  : ''
              }
              <div class="predictive-search__item-content predictive-search__item-content--centered">
                <span class="predictive-search__fuzzy-label">Meinten Sie vielleicht</span>
                <p class="predictive-search__item-heading h5">${this.escapeHTML(match.title)}</p>
              </div>
            </a>
          </li>`;
      })
      .join('');

    return `
      <div class="predictive-search__result-group predictive-search__fuzzy" data-fuzzy-suggestions>
        <div>
          <h2 class="predictive-search__heading text-body caption-with-letter-spacing">Vorschläge</h2>
          <ul class="predictive-search__results-list list-unstyled" role="group" aria-label="Vorschläge für ${this.escapeHTML(
            searchTerm
          )}">
            ${items}
          </ul>
        </div>
      </div>`;
  }

  escapeHTML(value) {
    return value
      .toString()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
